import type HyperDXType from "@hyperdx/browser";
import type { Attributes } from "@opentelemetry/api";

let initialized = false;
let initPromise: Promise<void> | null = null;
let HyperDX: typeof HyperDXType | null = null;

interface InitOptions {
  apiKey: string;
  service: string;
  url?: string;
  globalAttributes?: Record<string, string>;
}

// Events fired between the first render and the moment the lazy HyperDX
// chunk resolves are queued here, then flushed once init completes. This
// preserves the previous (sync init) behavior of capturing first-render
// crashes through ErrorBoundary.componentDidCatch → recordException.
type PendingCall =
  | { kind: "action"; name: string; attributes?: Attributes }
  | { kind: "exception"; error: unknown; attributes?: Attributes }
  | { kind: "boundary"; boundary: unknown };

const MAX_QUEUE = 200;
const pending: PendingCall[] = [];

function enqueue(call: PendingCall): void {
  if (pending.length < MAX_QUEUE) pending.push(call);
}

function flush(): void {
  if (!HyperDX) return;
  const sdk = HyperDX;
  for (const call of pending) {
    try {
      if (call.kind === "action") {
        sdk.addAction(call.name, call.attributes);
      } else if (call.kind === "exception") {
        sdk.recordException(call.error, call.attributes);
      } else {
        sdk.attachToReactErrorBoundary(call.boundary);
      }
    } catch {
      // Drop this individual queued call; keep flushing the rest so one
      // bad event doesn't poison the whole buffer.
    }
  }
  pending.length = 0;
}

export function initObservability(options: InitOptions): Promise<void> {
  if (initialized) return Promise.resolve();
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const { apiKey, service, url, globalAttributes } = options;

    try {
      const mod = await import(
        /* webpackChunkName: "hyperdx" */ "@hyperdx/browser"
      );
      HyperDX = mod.default;

      HyperDX.init({
        apiKey,
        service,
        consoleCapture: false,
        advancedNetworkCapture: false,
        disableReplay: true,
        ...(url
          ? {
              url,
              tracesUrl: `${url}/v1/traces`,
              logsUrl: `${url}/v1/logs`,
            }
          : {}),
      });

      if (globalAttributes) {
        HyperDX.setGlobalAttributes(globalAttributes);
      }

      initialized = true;
    } catch {
      // Failure path. Two possible causes:
      //   1. Chunk failed to load (ad blocker, network). HyperDX is null.
      //   2. Chunk loaded but HyperDX.init() / setGlobalAttributes() threw.
      //      HyperDX points at a loaded-but-unconfigured module — calling
      //      methods on it would throw or emit bogus telemetry.
      // Reset HyperDX to null so the exports' `if (HyperDX)` branch is
      // skipped, and mark initialized=true so they become permanent no-ops
      // instead of re-filling the queue up to MAX_QUEUE.
      pending.length = 0;
      HyperDX = null;
      initialized = true;
      return;
    }

    // Flush outside the init try/catch: a misbehaving queued event must
    // not corrupt the just-established init state for the rest of the
    // session. flush() itself isolates per-event failures.
    flush();
  })();

  return initPromise;
}

export function addAction(name: string, attributes?: Attributes): void {
  if (HyperDX) {
    HyperDX.addAction(name, attributes);
  } else if (!initialized) {
    enqueue({ kind: "action", name, attributes });
  }
}

export function recordException(error: unknown, attributes?: Attributes): void {
  if (HyperDX) {
    HyperDX.recordException(error, attributes);
  } else if (!initialized) {
    enqueue({ kind: "exception", error, attributes });
  }
}

export function attachToReactErrorBoundary(boundary: unknown): void {
  if (HyperDX) {
    HyperDX.attachToReactErrorBoundary(boundary);
  } else if (!initialized) {
    enqueue({ kind: "boundary", boundary });
  }
}
